/**
 * SupplierFeeManagementPage Component
 * 수수료 관리 (협력사) - 기본 수수료 설정 및 라이더별 수수료 관리
 */
import React from "react";
import { useSupplierFeeData } from "@/features/partner/supplierFee/riderToPartner/hooks/useSupplierFeeData";
import Loading from '@/shared/components/feedback/Loading';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import DefaultFeeSection from './DefaultFeeSection';
import FeeFilterSection from './FeeFilterSection';
import FeeTableRow from './FeeTableRow';
import SharedHistoryModal from '../../shared/components/HistoryModal';
import FeeApplyDateModal from '../../shared/components/FeeApplyDateModal';
import '@/shared/styles/common-table.css';

interface SupplierFeeManagementProps {
    selectedSupplierId?: string;
    onUnsavedChanges?: (hasChanges: boolean) => void;
}

const SupplierFeeManagementPage: React.FC<SupplierFeeManagementProps> = ({ selectedSupplierId, onUnsavedChanges }) => {
    const {
        loading,
        error,
        commissionData,
        filters,
        setFilters,
        statusFilter,
        isReadOnly,
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        showBatchFeeApplyModal,
        setShowBatchFeeApplyModal,
        batchFeeApplyDateInfo,
        setBatchFeeApplyDateInfo,
        handleRetry,
        handleBatchSave,
        handleSearch,
        handleQuickFilter,
        showHistoryModal,
        selectedRider,
        historyData,
        showFeeApplyModal,
        feeApplyDateInfo,
        handleCommissionTypeChange,
        handleAmountChange,
        handleSave,
        handleShowHistory,
        handleCloseHistoryModal,
        handleCloseFeeApplyModal,
    } = useSupplierFeeData({ selectedSupplierId, onUnsavedChanges });

    // ========================================
    // Render - 로딩 상태
    // ========================================
    if (loading) {
        return <Loading message="수수료 데이터를 불러오는 중..." />;
    }

    // ========================================
    // Render - 에러 상태
    // ========================================
    if (error) {
        return (
            <div className="supplier-selection-page">
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    backgroundColor: 'var(--color-white)',
                    border: '1px solid var(--color-gray-200)',
                    borderRadius: 'var(--border-radius-lg)',
                    padding: '40px'
                }}>
                    <i className="fas fa-exclamation-triangle" style={{
                        fontSize: '3rem',
                        color: 'var(--color-gray-400)',
                        marginBottom: '20px'
                    }}></i>
                    <p style={{
                        fontSize: '1.1rem',
                        color: 'var(--color-gray-600)',
                        marginBottom: '20px',
                        textAlign: 'center'
                    }}>
                        수수료 데이터를 불러오는데 실패했습니다.<br />
                        네트워크 연결을 확인하고 다시 시도해주세요.
                    </p>
                    <button
                        className="search-btn"
                        onClick={handleRetry}
                        style={{ marginLeft: 0 }}
                    >
                        <i className="fas fa-redo"></i>
                        <span>다시 시도</span>
                    </button>
                </div>
            </div>
        );
    }

    // ========================================
    // Render - 메인 UI
    // ========================================
    return (
        <div className="supplier-selection-page">
            {/* ========== 기본 수수료 설정 섹션 ========== */}
            <DefaultFeeSection
                selectedSupplierId={selectedSupplierId}
                isReadOnly={isReadOnly}
            />

            {/* 본사 계정 안내 메시지 */}
            {isReadOnly && (
                <div style={{
                    padding: '12px 16px',
                    marginBottom: '16px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    color: '#856404',
                    fontSize: '0.875rem'
                }}>
                    <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
                    본사 계정은 수수료 설정을 수정할 수 없습니다. 협력사 계정으로 로그인하여 수정해주세요.
                </div>
            )}

            {/* ========== 구분선 ========== */}
            <div style={{
                marginBottom: '24px',
                borderBottom: '2px solid var(--color-gray-300)'
            }}></div>

            {/* 필터 섹션 */}
            <FeeFilterSection
                filters={filters}
                statusFilter={statusFilter}
                onFilterChange={setFilters}
                onSearch={handleSearch}
                onQuickFilter={handleQuickFilter}
            />

            {/* 일괄 저장 버튼 */}
            {!isReadOnly && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: '16px',
                    padding: '12px 16px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{
                            fontSize: '0.875rem',
                            color: '#6c757d',
                            fontWeight: '500'
                        }}>
                            <i className="fas fa-edit" style={{ marginRight: '6px', color: '#ffc107' }}></i>
                            수정된 항목: <strong style={{ color: '#dc3545' }}>
                                {commissionData.filter(row => row.isEdited).length}건
                            </strong>
                        </span>
                        <button
                            onClick={handleBatchSave}
                            style={{
                                backgroundColor: commissionData.some(row => row.isEdited) ? '#04B374' : '#6c757d',
                                color: 'white',
                                border: 'none',
                                padding: '8px 20px',
                                borderRadius: '6px',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                cursor: commissionData.some(row => row.isEdited) ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 2px 4px rgba(40, 167, 69, 0.2)',
                                opacity: commissionData.some(row => row.isEdited) ? 1 : 0.6
                            }}
                            disabled={loading || !commissionData.some(row => row.isEdited)}
                        >
                            <i className="fas fa-save"></i>
                            전체 일괄 저장
                        </button>
                    </div>
                </div>
            )}

            {/* 테이블 섹션 */}
            <div className="table-container" style={{ overflowX: 'auto' }}>
                <table className="supplier-selection-table" style={{ minWidth: '800px' }}>
                    <thead>
                    <tr>
                        <th style={{ width: '60px', minWidth: '60px' }}>NO</th>
                        <th style={{ width: '100px', minWidth: '100px' }}>라이더ID</th>
                        <th style={{ width: '120px', minWidth: '120px' }}>라이더명</th>
                        <th style={{ width: '120px', minWidth: '120px' }}>수수료구분</th>
                        <th style={{ width: '100px', minWidth: '100px' }}>금액</th>
                        <th style={{ width: '120px', minWidth: '120px' }}>적용일</th>
                        <th style={{ width: '100px', minWidth: '100px' }}>변경 히스토리</th>
                        <th style={{ width: '80px', minWidth: '80px' }}>저장</th>
                    </tr>
                    </thead>
                    <tbody>
                    {currentItems.length === 0 ? (
                        <tr>
                            <td colSpan={8} className="text-center text-gray-600">
                                조회된 라이더가 없습니다.
                            </td>
                        </tr>
                    ) : (
                        currentItems.map((row, index) => (
                            <FeeTableRow
                                key={row.riderId}
                                row={row}
                                index={index}
                                currentPage={currentPage}
                                isReadOnly={isReadOnly}
                                onCommissionTypeChange={handleCommissionTypeChange}
                                onAmountChange={handleAmountChange}
                                onShowHistory={handleShowHistory}
                                onSave={handleSave}
                            />
                        ))
                    )}
                    </tbody>
                </table>
            </div>

            {/* 페이지네이션 */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
            />

            {/* 수수료 변경 히스토리 모달 */}
            <SharedHistoryModal
                isOpen={showHistoryModal}
                title="라이더 수수료 변경 이력"
                name={selectedRider}
                historyData={historyData}
                onClose={handleCloseHistoryModal}
                showPeriodColumn={false}
            />

            {/* 개별 저장 적용 예정일 모달 */}
            {feeApplyDateInfo && (
                <FeeApplyDateModal
                    isOpen={showFeeApplyModal}
                    applyStartDate={feeApplyDateInfo.applyStartDate}
                    isImmediateApply={feeApplyDateInfo.isImmediateApply}
                    onClose={handleCloseFeeApplyModal}
                />
            )}

            {/* 일괄 저장 적용 예정일 모달 */}
            {batchFeeApplyDateInfo && (
                <FeeApplyDateModal
                    isOpen={showBatchFeeApplyModal}
                    applyStartDate={batchFeeApplyDateInfo.applyStartDate}
                    isImmediateApply={batchFeeApplyDateInfo.isImmediateApply}
                    onClose={() => {
                        setShowBatchFeeApplyModal(false);
                        setBatchFeeApplyDateInfo(null);
                    }}
                />
            )}
        </div>
    );
};

export default SupplierFeeManagementPage;
